'use client';

import { motion } from 'framer-motion';
import { CircleSlash, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SetupChoiceOptionRow } from '@/components/setup/experience/SetupChoiceOptionRow';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { SetupConnectDisclaimer } from '@/components/setup/experience/SetupConnectFormShell';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';

interface SetupCheckoutUpsellStepProps {
  value: boolean | null;
  onSelect: (hasUpsell: boolean) => void;
  onContinue: () => void;
  onBack: () => void;
}

/** Q2 of the guided funnel generation — add a one-click post-purchase upsell? */
export function SetupCheckoutUpsellStep({
  value,
  onSelect,
  onContinue,
  onBack,
}: SetupCheckoutUpsellStepProps) {
  const { t } = useTranslation('setup');

  return (
    <SetupStepSection
      title={t('experience.checkout.upsell.title')}
      description={t('experience.checkout.upsell.description')}
      footer={
        <SetupStepFooter
          backLabel={t('footer.back')}
          onBack={onBack}
          primaryLabel={t('footer.continue')}
          onPrimary={onContinue}
          primaryDisabled={value === null}
        />
      }
    >
      <motion.ul
        className="m-0 flex list-none flex-col gap-3 p-0"
        role="listbox"
        aria-label={t('experience.checkout.upsell.title')}
        variants={SETUP_PATH_MOTION.list}
        initial="hidden"
        animate="show"
      >
        <SetupChoiceOptionRow
          icon={
            <Gift
              className="h-[1.375rem] w-[1.375rem] text-[color-mix(in_oklch,var(--foreground)_70%,var(--muted-foreground))]"
              aria-hidden
            />
          }
          name={t('experience.checkout.upsell.yes.name')}
          hint={t('experience.checkout.upsell.yes.hint')}
          badge={t('experience.checkout.upsell.recommendedBadge')}
          proof={t('experience.checkout.upsell.yes.proof')}
          selected={value === true}
          onSelect={() => onSelect(true)}
        />
        <SetupChoiceOptionRow
          icon={
            <CircleSlash
              className="h-[1.375rem] w-[1.375rem] text-[color-mix(in_oklch,var(--foreground)_70%,var(--muted-foreground))]"
              aria-hidden
            />
          }
          name={t('experience.checkout.upsell.no.name')}
          hint={t('experience.checkout.upsell.no.hint')}
          selected={value === false}
          onSelect={() => onSelect(false)}
        />
      </motion.ul>

      {value === true ? (
        <div className="mt-4">
          <SetupConnectDisclaimer>{t('experience.checkout.upsell.previewNote')}</SetupConnectDisclaimer>
        </div>
      ) : null}
    </SetupStepSection>
  );
}
